import React, { useState, useEffect } from 'react';
import { ClipboardList, Plus, Trash2, Loader2, ArrowUp, ArrowDown, X, Save, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { value: 'text', label: 'Open text' },
  { value: 'yes_no', label: 'Yes / no' },
  { value: 'multi_choice', label: 'Multi-choice' },
  { value: 'short_list', label: 'Short list' },
];

const blankQuestion = () => ({
  id: `q_${Math.random().toString(36).slice(2, 9)}`,
  label: '',
  type: 'text',
  options: [],
  required: false,
});

const QuestionRow = ({ q, idx, total, onChange, onRemove, onMove }) => {
  const needsOptions = q.type === 'multi_choice' || q.type === 'short_list';
  return (
    <div className="border border-[#E5E7EB] rounded p-2 space-y-1.5 bg-white" data-testid={`survey-q-${idx}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-1.5">
          <input value={q.label} onChange={(e) => onChange({ ...q, label: e.target.value })}
            placeholder="Question text (e.g. How can we help your family this week?)"
            className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid={`survey-q-label-${idx}`} />
          <div className="flex items-center gap-2">
            <select value={q.type} onChange={(e) => onChange({ ...q, type: e.target.value, options: [] })}
              className="px-2 py-1 border border-[#E5E7EB] rounded text-xs"
              data-testid={`survey-q-type-${idx}`}>
              {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <label className="inline-flex items-center gap-1 text-[11px] text-[#4B5563] cursor-pointer">
              <input type="checkbox" checked={q.required}
                onChange={(e) => onChange({ ...q, required: e.target.checked })}
                data-testid={`survey-q-required-${idx}`} /> required
            </label>
          </div>
          {needsOptions && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Options (one per line)</p>
              <textarea value={(q.options || []).join('\n')}
                onChange={(e) => onChange({ ...q, options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                rows={3}
                placeholder="Mornings\nAfternoons\nEvenings"
                className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none resize-none font-mono"
                data-testid={`survey-q-options-${idx}`} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => onMove(idx, -1)} disabled={idx === 0}
            className="text-[#9CA3AF] hover:text-[#0A0A0A] disabled:opacity-30" data-testid={`survey-q-up-${idx}`}>
            <ArrowUp className="w-3 h-3" />
          </button>
          <button onClick={() => onMove(idx, 1)} disabled={idx === total - 1}
            className="text-[#9CA3AF] hover:text-[#0A0A0A] disabled:opacity-30" data-testid={`survey-q-down-${idx}`}>
            <ArrowDown className="w-3 h-3" />
          </button>
          <button onClick={() => onRemove(idx)}
            className="text-red-600 hover:text-red-800" data-testid={`survey-q-remove-${idx}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SurveySchemaEditor = ({ open, zoneId = null, zoneName = null, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [source, setSource] = useState('global'); // 'global' | 'zone'

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = zoneId
          ? `${BACKEND_URL}/api/admin/outreach/survey-schema?zone_id=${zoneId}`
          : `${BACKEND_URL}/api/admin/outreach/survey-schema`;
        const r = await axios.get(url, { withCredentials: true });
        if (cancelled) return;
        setQuestions(r.data?.schema || []);
        setSource(r.data?.source || 'global');
        setError('');
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load survey schema');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, zoneId]);

  if (!open) return null;

  const updateQ = (idx, next) => {
    setQuestions((prev) => prev.map((q, i) => i === idx ? next : q));
  };
  const removeQ = (idx) => setQuestions((prev) => prev.filter((_, i) => i !== idx));
  const addQ = () => setQuestions((prev) => [...prev, blankQuestion()]);
  const moveQ = (idx, delta) => setQuestions((prev) => {
    const next = [...prev];
    const t = idx + delta;
    if (t < 0 || t >= next.length) return prev;
    [next[idx], next[t]] = [next[t], next[idx]];
    return next;
  });

  const save = async () => {
    setSaving(true); setError('');
    try {
      const body = { schema: questions.filter(q => q.label?.trim()) };
      const url = zoneId
        ? `${BACKEND_URL}/api/admin/outreach/zones/${zoneId}/survey-schema`
        : `${BACKEND_URL}/api/admin/outreach/survey-schema/global`;
      await axios.put(url, body, { withCredentials: true });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save survey schema');
    } finally { setSaving(false); }
  };

  const clearZoneOverride = async () => {
    if (!zoneId) return;
    if (!window.confirm('Clear this zone\u2019s custom survey and fall back to the global default?')) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/outreach/zones/${zoneId}/survey-schema`,
        { schema: [] }, { withCredentials: true });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to clear override');
    } finally { setSaving(false); }
  };

  const seedCommunityInterest = async () => {
    if (zoneId) return;
    if (!window.confirm(
      'Replace the global survey with the structured Community Interest Survey?\n\n' +
      'This loads 9 questions covering the 7 NHF program categories ' +
      '(Food, Hygiene, Youth, Safety, Events, Education, Outreach) plus a top-priority pick ' +
      'and a write-in. Your current questions will be replaced.'
    )) return;
    setSaving(true); setError('');
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/outreach/survey-schema/seed-community-interest`,
        {}, { withCredentials: true });
      // Reload from server so the editor shows the seeded schema
      const reload = await axios.get(`${BACKEND_URL}/api/admin/outreach/survey-schema`,
        { withCredentials: true });
      setQuestions(reload.data?.schema || []);
      setSource(reload.data?.source || 'global');
      onSaved && onSaved();
      window.alert(`Seeded — ${r.data?.question_count || 0} questions loaded.`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to seed Community Interest Survey');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="survey-schema-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h3 className="font-heading text-base font-bold text-[#0A0A0A] flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-[#BA1826]" />
              {zoneId ? `Survey for ${zoneName || 'this zone'}` : 'Global default survey'}
            </h3>
            <p className="text-[11px] text-[#4B5563]">
              {zoneId
                ? (source === 'zone'
                    ? 'This zone has a custom survey. Clear it to fall back to the global default.'
                    : 'No override yet — you\u2019re editing a fresh copy of the global default.')
                : 'Default survey questions shown on every log form that doesn\u2019t have a per-zone override.'}
            </p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="survey-schema-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
          ) : (
            <>
              {questions.length === 0 && (
                <p className="text-xs text-[#9CA3AF] text-center py-4">
                  No questions yet. Add the first one — short, neighborly, never demanding.
                </p>
              )}
              {questions.map((q, i) => (
                <QuestionRow key={q.id || i} q={q} idx={i} total={questions.length}
                  onChange={(next) => updateQ(i, next)}
                  onRemove={removeQ}
                  onMove={moveQ} />
              ))}
              <button onClick={addQ}
                className="w-full text-xs px-3 py-2 border border-dashed border-[#E5E7EB] rounded text-[#4B5563] hover:bg-[#F8F9FA] inline-flex items-center justify-center gap-1.5"
                data-testid="survey-schema-add-q">
                <Plus className="w-3 h-3" /> Add question
              </button>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#E5E7EB] flex items-center justify-end gap-2 flex-wrap">
          {zoneId && source === 'zone' && (
            <button onClick={clearZoneOverride} disabled={saving}
              className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded mr-auto"
              data-testid="survey-schema-clear-override">
              Clear override
            </button>
          )}
          {!zoneId && (
            <button onClick={seedCommunityInterest} disabled={saving}
              className="text-xs px-3 py-1.5 border border-[#BA1826]/30 text-[#BA1826] rounded hover:bg-[#FFF8F8] disabled:opacity-50 inline-flex items-center gap-1.5 mr-auto"
              data-testid="survey-schema-seed-community-interest">
              <Sparkles className="w-3 h-3" /> Seed Community Interest Survey
            </button>
          )}
          <button onClick={onClose}
            className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded">Cancel</button>
          <button onClick={save} disabled={saving}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="survey-schema-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save survey
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveySchemaEditor;
